import { useQuery } from '@tanstack/react-query';
import { routeTrackingAPI } from '@/lib/supabase/routeTracking';
import { getServiceEventsByAssignmentIds } from '@/lib/supabase/serviceEvents';
import type { RouteTrackerItem, RouteRecallDetail } from '@/types/routeTracking';
import type { ServiceEventCompletion } from '@/types/serviceEvents';
import { serviceEventKeys } from './useServiceEventCompletion';
import { getStopLocationKey } from '@/lib/manualStops/locationKey';

export interface DailyRouteSummary {
  route: RouteTrackerItem;
  events: Map<string, ServiceEventCompletion>;
}

export interface DailySummaryStop {
  dealerName: string;
  // Full street-level address parts carried so every stop-level surface (page + print)
  // can render street, city, state, zip without re-deriving from recalls[0].
  dealerAddress?: string;
  dealerCity: string;
  dealerRegion: string;
  dealerPostalCode?: string;
  recalls: RouteRecallDetail[];
  completedCount: number;
  arrivedAt?: string;
  departedAt?: string;
  status: 'all_complete' | 'partial' | 'skipped' | 'pending';
}

// Group recalls in route order, by dealer, so the page can render a plan-vs-actuals table.
// The route tracker only exposes a flat recalls array; the visual "stop" is the dealer.
//
// When the `events` map is provided, stop-level Arrived/Departed times come ONLY from
// iOS-captured `started_at`/`completed_at`. We previously fell back to `recall.assignedAt`
// (admin timestamp) which produced misleading "started at 8am yesterday" labels for
// web-only updates. Honest empty is better than confidently wrong.
/** Group key: household for customer stops, dealer name + city otherwise. */
function stopGroupKey(recall: RouteRecallDetail, isCustomer: boolean): string {
  if (!isCustomer) return `${recall.dealerName}__${recall.dealerCity}`;
  return getStopLocationKey({
    stop_kind: 'customer',
    contact_id: recall.contactId,
    dealer_address: recall.dealerAddress,
    address_line2: recall.addressLine2,
    dealer_postal_code: recall.dealerPostalCode
  });
}

// eslint-disable-next-line max-lines-per-function
export function groupByStop(
  route: RouteTrackerItem,
  events?: Map<string, ServiceEventCompletion>
): DailySummaryStop[] {
  const groups = new Map<string, DailySummaryStop>();
  for (const recall of route.recalls) {
    // Customer stops are all named 'Customer stop' — one group per household.
    const isCustomer = recall.stopKind === 'customer';
    const key = stopGroupKey(recall, isCustomer);
    const street =
      isCustomer && recall.addressLine2
        ? `${recall.dealerAddress}, ${recall.addressLine2}`
        : recall.dealerAddress;
    let group = groups.get(key);
    if (!group) {
      group = {
        dealerName: recall.dealerName,
        ...(street && { dealerAddress: street }),
        dealerCity: recall.dealerCity,
        dealerRegion: recall.dealerRegion,
        ...(recall.dealerPostalCode && { dealerPostalCode: recall.dealerPostalCode }),
        recalls: [],
        completedCount: 0,
        status: 'pending'
      };
      groups.set(key, group);
    }
    group.recalls.push(recall);
    if (recall.status === 'completed') {
      group.completedCount += 1;
      const ev = events?.get(recall.assignmentId);
      // arrivedAt only comes from real iOS start times — no admin-timestamp fallback.
      if (ev?.started_at && (!group.arrivedAt || ev.started_at < group.arrivedAt)) {
        group.arrivedAt = ev.started_at;
      }
      // departedAt prefers iOS completed_at but accepts recall.completedAt (honest either way).
      const finished = ev?.completed_at || recall.completedAt;
      if (finished && (!group.departedAt || finished > group.departedAt)) {
        group.departedAt = finished;
      }
    }
  }
  return Array.from(groups.values()).map(g => {
    // "Skipped" should mean someone explicitly released work, not just "nothing happened
    // yet." A stop with no completions is "Skipped" only if there's evidence of a release
    // (cancelled status or releaseStatus set on any recall); otherwise it's "Pending" —
    // either the tech hasn't reached the stop or hasn't updated yet.
    const anyReleased = g.recalls.some(r => r.status === 'cancelled' || !!r.releaseStatus);
    const status: DailySummaryStop['status'] =
      g.completedCount === g.recalls.length
        ? 'all_complete'
        : g.completedCount > 0
          ? 'partial'
          : anyReleased
            ? 'skipped'
            : 'pending';
    return { ...g, status };
  });
}

export const dailyRouteSummaryKeys = {
  all: ['daily-route-summary'] as const,
  byTechDate: (technicianId: string, date: string) =>
    [...dailyRouteSummaryKeys.all, technicianId, date] as const
};

interface UseDailyRouteSummaryArgs {
  technicianId: string | null;
  date: string | null; // yyyy-mm-dd
}

export function useDailyRouteSummary({ technicianId, date }: UseDailyRouteSummaryArgs) {
  return useQuery({
    queryKey: dailyRouteSummaryKeys.byTechDate(technicianId ?? '', date ?? ''),
    enabled: !!technicianId && !!date,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<DailyRouteSummary | null> => {
      if (!technicianId || !date) return null;

      // Use the same backing fetch as Route Tracker so the screen + print stay consistent.
      const routes = await routeTrackingAPI.getRoutesFromTable({
        dateRange: { start: date, end: date, preset: 'custom' },
        technicianId
      });
      const route = routes.find(r => r.technicianId === technicianId) ?? routes[0] ?? null;
      if (!route) return null;

      const assignmentIds = route.recalls.map(r => r.assignmentId).filter(Boolean);
      const events = await getServiceEventsByAssignmentIds(assignmentIds);

      // Pre-prime the per-assignment cache used by the drawer so navigating from the
      // summary back to Route Tracker has the data warm.
      // (No-op if the assignmentId isn't queried again.)
      return { route, events };
    }
  });
}

// Expose the per-assignment cache priming for callers that want to warm the drawer cache.
export { serviceEventKeys };
